import { useCallback, useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../lib/api';
import { formatNumber, formatPercent } from '../lib/utils';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

export function ImportMasterPriceListDialog({ open, onOpenChange, dealId, onImported }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(() => new Set());

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getMasterPriceListItems({ state: 'active' });
      setItems(res.data || []);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to load master price list');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setSearch('');
    setSelected(new Set());
    loadItems();
  }, [open, loadItems]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => {
      const hay = [it.name, it.category, it.vendor, it.description_plain]
        .map((x) => String(x || '').toLowerCase())
        .join(' ');
      return hay.includes(q);
    });
  }, [items, search]);

  const toggle = (id, checked) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const handleImport = async () => {
    const ids = [...selected];
    if (!ids.length || !dealId) return;
    setImporting(true);
    try {
      const res = await api.importMasterPriceListToDeal(dealId, { item_ids: ids });
      toast.success(`Imported ${ids.length} line item${ids.length === 1 ? '' : 's'} from master price list`);
      onImported?.(res.data);
      onOpenChange(false);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to import from master price list');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="import-master-price-list-dialog">
        <DialogHeader>
          <DialogTitle>Import from Master Price List</DialogTitle>
          <DialogDescription>
            Select one or more catalog items. Values are copied into the deal and remain editable; later catalog changes do not affect imported rows.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, category, or vendor…"
            className="pl-9"
            data-testid="import-master-price-list-search"
          />
        </div>

        <div className="max-h-[min(50vh,360px)] overflow-auto rounded-md border border-gray-200">
          {loading ? (
            <p className="p-4 text-sm text-gray-500">Loading catalog…</p>
          ) : filtered.length === 0 ? (
            <p className="p-4 text-sm text-gray-500">No active master price list items match your search.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {filtered.map((it) => {
                const checked = selected.has(it.id);
                return (
                  <li key={it.id}>
                    <label className="flex cursor-pointer items-start gap-3 px-3 py-2.5 hover:bg-slate-50">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(v) => toggle(it.id, v === true)}
                        className="mt-0.5"
                        data-testid={`import-master-item-${it.id}`}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-gray-900">{it.name}</span>
                        <span className="mt-0.5 block text-xs text-gray-500">
                          {[it.category, it.vendor].filter(Boolean).join(' · ') || 'No category'}
                          {' · '}
                          {formatNumber(it.revenue_unit)} {it.revenue_currency}
                          {' / '}
                          {formatNumber(it.cost_unit)} {it.cost_currency}
                          {it.margin_pct != null ? ` · ${formatPercent(it.margin_pct)} margin` : ''}
                        </span>
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={importing}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleImport}
            disabled={importing || selected.size === 0}
            data-testid="import-master-price-list-confirm"
          >
            {importing ? 'Importing…' : `Import selected (${selected.size})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
